export interface CalendarDateRange {
  start: Date;
  end: Date;
}

export interface InclusiveTimestampRange {
  start: string;
  end: string;
}

export interface DateOnlyRange {
  startDate: string;
  endDate: string;
}

export const isCalendarDateRangeValid = ({ start, end }: CalendarDateRange): boolean => {
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;

  const normalizedStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const normalizedEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  return normalizedStart.getTime() <= normalizedEnd.getTime();
};

const assertValidRange = ({ start, end }: CalendarDateRange) => {
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    throw new RangeError("Date range contains an invalid date");
  }

  if (!isCalendarDateRangeValid({ start, end })) {
    throw new RangeError("Date range start must not be after its end");
  }
};

const copyAtStartOfDay = (date: Date) => {
  const result = new Date(date.getTime());
  result.setHours(0, 0, 0, 0);
  return result;
};

const copyAtEndOfDay = (date: Date) => {
  const result = new Date(date.getTime());
  result.setHours(23, 59, 59, 999);
  return result;
};

/**
 * Converts calendar days selected in the browser into inclusive timestamp
 * boundaries for the existing Supabase function contracts (`gte` / `lte`).
 */
export const toInclusiveTimestampRange = (range: CalendarDateRange): InclusiveTimestampRange => {
  assertValidRange(range);

  return {
    start: copyAtStartOfDay(range.start).toISOString(),
    end: copyAtEndOfDay(range.end).toISOString()
  };
};

/**
 * Formats a browser-local calendar day without converting it to UTC first.
 * This avoids the previous-day shift caused by `toISOString().split("T")[0]`.
 */
export const toLocalDateString = (date: Date): string => {
  if (Number.isNaN(date.getTime())) {
    throw new RangeError("Cannot format an invalid date");
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const toDateOnlyRange = (range: CalendarDateRange): DateOnlyRange => {
  assertValidRange(range);

  return {
    startDate: toLocalDateString(range.start),
    endDate: toLocalDateString(range.end)
  };
};
